import React, { useState } from 'react';
import { Box, Typography, Divider, Dialog, DialogContent, IconButton, Button, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import StreetviewIcon from '@mui/icons-material/Streetview';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { styled } from '@mui/system';

// Estilo para el contenedor de la imagen
const ImageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '180px', // Altura fija para la imagen
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[100], // Fondo suave si no hay imagen o está cargando
  marginBottom: theme.spacing(2),
  boxShadow: theme.shadows[1], // Sombra sutil
  cursor: 'pointer', // Añade cursor de puntero para indicar que es clickeable
  '&:hover': { // Efecto hover para indicar interactividad
    opacity: 0.8,
  },
}));

function SpaceDetailsPanel({ selectedSpace, proposalCart, onToggleCart }) {
  // Nuevo estado para controlar la visibilidad del diálogo de la imagen
  const [openImageDialog, setOpenImageDialog] = useState(false);

  // Función para abrir el diálogo
  const handleImageClick = () => {
    if (selectedSpace && selectedSpace.url_foto) {
      setOpenImageDialog(true);
    }
  };

  // Función para cerrar el diálogo
  const handleCloseImageDialog = () => {
    setOpenImageDialog(false);
  };

  if (!selectedSpace) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" color="primary" gutterBottom>
          Detalles del Espacio
        </Typography>
        <ImageContainer>
          <Typography variant="body2" color="text.secondary">
            Haz clic en un marcador para ver los detalles.
          </Typography>
        </ImageContainer>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="text.secondary">
            Selecciona un espacio en el mapa para visualizar sus características y foto aquí.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" color="primary" gutterBottom>
        Detalles del Espacio
      </Typography>

      {/* Área de la Foto - Ahora con onClick para abrir el diálogo */}
      <ImageContainer onClick={handleImageClick}> {/* Añade el onClick aquí */}
        {selectedSpace.url_foto ? (
          <img
            src={selectedSpace.url_foto}
            alt={`Foto de ${selectedSpace.Codigo}`}
            style={{ 
              maxWidth: '100%', 
              maxHeight: '100%', 
              objectFit: 'contain', 
              borderRadius: '4px' 
            }}
            onError={(e) => { 
                e.target.onerror = null; 
                e.target.src="/placeholder-image.png"; 
                e.target.style.objectFit = 'cover'; 
            }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No hay foto disponible
          </Typography>
        )}
      </ImageContainer>
      
      <Divider sx={{ my: 2 }} />

      {/* Detalles del Espacio */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            {selectedSpace.Codigo}
          </Typography>
          <Chip 
            label={selectedSpace.Estado || 'Desconocido'} 
            size="small" 
            color={selectedSpace.Estado === 'Disponible' ? 'success' : selectedSpace.Estado === 'Ocupada' ? 'error' : 'warning'} 
            variant="outlined" 
            sx={{ fontWeight: 'bold' }} 
          />
        </Box>

        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <span style={{ fontWeight: 'bold' }}>Tipo:</span> {selectedSpace.Tipo_Espacio}
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <span style={{ fontWeight: 'bold' }}>Dimensiones:</span> {selectedSpace.Tamano || `${selectedSpace.Ancho || 0}m x ${selectedSpace.Alto || 0}m`}
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <span style={{ fontWeight: 'bold' }}>Zona:</span> {selectedSpace.Zona} ({selectedSpace.Provincia})
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <span style={{ fontWeight: 'bold' }}>Dirección:</span> {selectedSpace.Direccion}
        </Typography>
        {selectedSpace.PrecioBase && (
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <span style={{ fontWeight: 'bold' }}>Precio Base:</span> RD$ {Number(selectedSpace.PrecioBase).toLocaleString()}
          </Typography>
        )}
        {(selectedSpace.FlujoDiario || selectedSpace.PuntosInteres) && (
          <Box sx={{ mt: 1, p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
            <Typography variant="caption" display="block" color="text.secondary">Datos de Impacto:</Typography>
            {selectedSpace.FlujoDiario && <Typography variant="body2" fontSize="0.8rem">• Flujo: {Number(selectedSpace.FlujoDiario).toLocaleString()} veh./día</Typography>}
            {selectedSpace.Iluminacion && <Typography variant="body2" fontSize="0.8rem">• Iluminada: Sí ({selectedSpace.HorarioIluminacion || 'N/A'})</Typography>}
            {selectedSpace.PuntosInteres && <Typography variant="body2" fontSize="0.8rem">• Cerca de: {selectedSpace.PuntosInteres}</Typography>}
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 1.5 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {onToggleCart && (
          <Button
            variant={proposalCart?.has(selectedSpace.Codigo) ? "contained" : "outlined"}
            color={proposalCart?.has(selectedSpace.Codigo) ? "success" : "primary"}
            fullWidth
            startIcon={proposalCart?.has(selectedSpace.Codigo) ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
            onClick={() => onToggleCart(selectedSpace.Codigo)}
            sx={{
              fontWeight: 700,
              py: 1,
              fontSize: '0.85rem',
              ...(proposalCart?.has(selectedSpace.Codigo) && {
                boxShadow: '0 2px 8px rgba(46,125,50,0.25)',
              }),
            }}
          >
            {proposalCart?.has(selectedSpace.Codigo) ? 'Seleccionada ✓' : 'Seleccionar'}
          </Button>
        )}
        <Button 
          variant="outlined" 
          size="small" 
          startIcon={<StreetviewIcon />} 
          href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${selectedSpace.Latitud},${selectedSpace.Longitud}`} 
          target="_blank"
        >
          Street View
        </Button>
      </Box>

      {/* Diálogo para ver la imagen a tamaño completo */}
      <Dialog
        open={openImageDialog}
        onClose={handleCloseImageDialog}
        maxWidth="md" // Define el ancho máximo del diálogo
        fullWidth // Ocupa todo el ancho disponible hasta maxWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}> {/* Padding 0 para que la imagen ocupe todo */}
          <IconButton
            aria-label="close"
            onClick={handleCloseImageDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
              zIndex: 1 // Asegura que el botón esté sobre la imagen
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedSpace.url_foto ? (
            <img
              src={selectedSpace.url_foto}
              alt={`Foto de ${selectedSpace.Codigo} (completa)`}
              style={{ 
                width: '100%', 
                height: 'auto', 
                display: 'block', // Elimina espacio extra debajo de la imagen
                maxHeight: '80vh', // Limita la altura de la imagen al 80% del viewport height
                objectFit: 'contain' // Asegura que la imagen se ajuste sin recortarse
              }}
              onError={(e) => { 
                  e.target.onerror = null; 
                  e.target.src="/placeholder-image.png"; 
                  e.target.style.objectFit = 'cover'; 
              }}
            />
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No hay foto disponible para mostrar.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default SpaceDetailsPanel;